// src/components/HistoryPage.jsx
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import HistoryList from './HistoryList';
import Map from './Map';
import ReceiptRedemptionModal from './ReceiptRedemptionModal';
import { useOptionalAppSession } from '../hooks/AppSessionContext';

/* ---------- HARTE ABSICHERUNG -> mobil-sicher ---------- */
// Falls irgendein Helper/Object-Spread in aelteren WebViews/Browsern intern Object.entries(null)
// aufruft, fangen wir das global ab. Bei null/undefined -> einfach leeres Array zurueck.
(() => {
  const _entries = Object.entries;
  Object.entries = function (obj) {
    if (obj == null) return [];
    if (typeof _entries === 'function') {
      try { return _entries.call(Object, obj); } catch { return []; }
    }
    const keys = Object.keys(obj);
    const out = [];
    for (let i = 0; i < keys.length; i++) out.push([keys[i], obj[keys[i]]]);
    return out;
  };
})();
/* ------------------------------------------------------- */

export default function HistoryPage({
  displayEntries,
  safeUpdate,
  handleRemove,
  userColors,
  filter,
  setFilter,
  customRange,
  setCustomRange,
  reloadEntries,
  applyReceiptRedemptionResult
}) {
  const base = Array.isArray(displayEntries) ? displayEntries : [];
  const colors = (userColors && typeof userColors === 'object') ? userColors : {};
  const currentFilter = (typeof filter === 'string') ? filter : 'all';
  const range = (customRange && typeof customRange === 'object')
    ? { from: customRange.from || '', to: customRange.to || '' }
    : { from: '', to: '' };

  const setRangeFrom = useCallback((val) => {
    if (typeof setCustomRange !== 'function') return;
    setCustomRange(prev => {
      const p = (prev && typeof prev === 'object') ? prev : {};
      return { from: String(val || ''), to: String(p.to || '') };
    });
  }, [setCustomRange]);

  const setRangeTo = useCallback((val) => {
    if (typeof setCustomRange !== 'function') return;
    setCustomRange(prev => {
      const p = (prev && typeof prev === 'object') ? prev : {};
      return { from: String(p.from || ''), to: String(val || '') };
    });
  }, [setCustomRange]);

  const filtered = useMemo(() => {
    if (!base.length) return [];

    const now = new Date();
    const todayY = now.getFullYear();
    const todayM = now.getMonth();
    const todayD = now.getDate();

    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    const yest = new Date(now);
    yest.setDate(todayD - 1);

    let from = null;
    let to = null;
    if (currentFilter === 'custom' && range.from) {
      from = new Date(range.from);
      from.setHours(0, 0, 0, 0);
      to = range.to ? new Date(range.to) : new Date(range.from);
      to.setHours(23, 59, 59, 999);
    }

    const out = [];
    for (let i = 0; i < base.length; i++) {
      const e = base[i];
      if (!e || !e.timestamp) continue;
      const dt = new Date(e.timestamp);
      let keep = false;
      switch (currentFilter) {
        case 'today':
          keep = sameDay(dt, now);
          break;
        case 'yesterday':
          keep = sameDay(dt, yest);
          break;
        case 'month':
          keep = dt.getFullYear() === todayY && dt.getMonth() === todayM;
          break;
        case 'custom':
          keep = (from && to) ? (dt >= from && dt <= to) : false;
          break;
        case 'all':
        default:
          keep = true;
      }
      if (keep) out.push(e);
    }
    return out;
  }, [base, currentFilter, range.from, range.to]);

  const [selectedMap, setSelectedMap] = useState({});
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptEntryIds, setReceiptEntryIds] = useState([]);
  const sessionContext = useOptionalAppSession();
  const receiptRedemptionsEnabled = sessionContext?.session?.features?.receipt_redemptions === true;

  const onToggleSelect = useCallback((id) => {
    setSelectedMap(prev => {
      const prevObj = (prev && typeof prev === 'object') ? prev : {};
      const next = {};
      const keys = Object.keys(prevObj);
      for (let i = 0; i < keys.length; i++) next[keys[i]] = true;
      if (next[id]) delete next[id]; else next[id] = true;
      return next;
    });
  }, []);

  const onSelectAll = useCallback(() => {
    const next = {};
    for (let i = 0; i < filtered.length; i++) {
      const e = filtered[i];
      if (!e) continue;
      const st = (e.status === undefined || e.status === null) ? 'open' : e.status;
      if (st === 'open') next[e.id] = true;
    }
    setSelectedMap(next);
  }, [filtered]);

  const onClearSelection = useCallback(() => {
    setSelectedMap({});
  }, []);

  const onRedeem = useCallback(async (id) => {
    if (!receiptRedemptionsEnabled) {
      await safeUpdate(id, { status: 'redeemed' });
      if (typeof reloadEntries === 'function') {
        await reloadEntries();
      }
      return;
    }

    setReceiptEntryIds([Number(id)]);
    setReceiptModalOpen(true);
  }, [receiptRedemptionsEnabled, reloadEntries, safeUpdate]);

  const onBulkRedeem = useCallback(async () => {
    const ids = Object.keys(selectedMap || {}).map(k => Number(k));
    if (!ids.length) return;

    if (!receiptRedemptionsEnabled) {
      await Promise.all(ids.map(id => safeUpdate(id, { status: 'redeemed' })));
      setSelectedMap({});
      if (typeof reloadEntries === 'function') {
        await reloadEntries();
      }
      return;
    }

    setReceiptEntryIds(ids);
    setReceiptModalOpen(true);
  }, [receiptRedemptionsEnabled, reloadEntries, safeUpdate, selectedMap]);

  const selectedCount = useMemo(
    () => Object.keys(selectedMap || {}).length,
    [selectedMap]
  );

  const receiptExpectedAmount = useMemo(() => {
    const ids = new Set((receiptEntryIds || []).map(Number));
    if (!ids.size) return 0;
    return filtered
      .filter(entry => ids.has(Number(entry.id)))
      .reduce((sum, entry) => sum + Number(entry.amount || 0), 0);
  }, [filtered, receiptEntryIds]);

  const handleReceiptSuccess = useCallback(async (result) => {
    if (typeof applyReceiptRedemptionResult === 'function') {
      applyReceiptRedemptionResult(result);
    } else if (result?.session_patch && sessionContext?.mergeSessionPatch) {
      sessionContext.mergeSessionPatch(result.session_patch);
    }
    setSelectedMap({});
    setReceiptEntryIds([]);
    if (typeof reloadEntries === 'function') {
      Promise.resolve(reloadEntries()).catch(error => {
        console.warn('[Pfandpirat] Bonstatus ist gespeichert; Historie wird beim nächsten Abruf aktualisiert.', error);
      });
    }
  }, [applyReceiptRedemptionResult, reloadEntries, sessionContext]);

  useEffect(() => {
    if (typeof reloadEntries === 'function') {
      reloadEntries();
    }
  }, []);

  return (
    <main className="pfp-history-page">
      <ReceiptRedemptionModal
        isOpen={receiptRedemptionsEnabled && receiptModalOpen}
        expectedAmount={receiptExpectedAmount}
        selectedEntryIds={receiptEntryIds}
        title="Auswahl mit Pfandbon einloesen"
        onClose={() => setReceiptModalOpen(false)}
        onSuccess={handleReceiptSuccess}
      />
      <section className="pfp-history-hero" aria-label="Historie">
        <div>
          <span className="pfp-history-kicker">Logbuch</span>
          <h1>Historie & Schatzkarte</h1>
          <p>Pruefe deine Pfandfunde, waehle offene Eintraege aus und loese sie im Stapel ein.</p>
        </div>
      </section>

      <section className="pfp-history-filter" aria-label="Zeitraum filtern">
        <label htmlFor="pfp-history-filter-select">Zeitraum</label>
        <select
          id="pfp-history-filter-select"
          value={currentFilter}
          onChange={e => (typeof setFilter === 'function' ? setFilter(e.target.value) : null)}
        >
          <option value="today">Heute</option>
          <option value="yesterday">Gestern</option>
          <option value="month">Dieser Monat</option>
          <option value="all">Gesamt</option>
          <option value="custom">Benutzerdef.</option>
        </select>
        {currentFilter === 'custom' ? (
          <>
            <input type="date" value={range.from} onChange={e => setRangeFrom(e.target.value)} />
            <input type="date" value={range.to} onChange={e => setRangeTo(e.target.value)} />
          </>
        ) : null}
      </section>

      <section className="pfp-history-map-card" aria-label="Deine Schatzkarte">
        <div className="pfp-history-section-head">
          <div>
            <span className="pfp-history-kicker">Schatzkarte</span>
            <h2>Deine Schatzkarte</h2>
          </div>
          <strong>{filtered.length} Eintraege</strong>
        </div>

        {filtered.length > 0 ? (
          <div className="pfp-history-map-frame">
            <Map entries={filtered} colors={colors} />
          </div>
        ) : (
          <div className="pfp-history-empty-map">Keine Schatzkarten-Eintraege im gewaehlten Zeitraum.</div>
        )}
      </section>

      <section className="pfp-history-bulkbar" aria-label="Auswahlaktionen">
        <button className="pfand-button" onClick={onSelectAll}>Alle offenen auswaehlen</button>
        <button className="pfand-button" onClick={onClearSelection}>Auswahl aufheben</button>
        <button
          className="pfand-button"
          onClick={onBulkRedeem}
          disabled={selectedCount === 0}
          title={selectedCount === 0 ? 'Bitte zuerst Eintraege auswaehlen' : 'Ausgewaehlte Eintraege einloesen'}
        >
          {selectedCount > 0 ? `Auswahl einloesen (${selectedCount})` : 'Auswahl einloesen'}
        </button>
      </section>

      <section className="pfp-history-list-card" aria-label="Pfandhistorie">
        <div className="pfp-history-section-head">
          <div>
            <span className="pfp-history-kicker">Fundliste</span>
            <h2>Pfand-Eintraege</h2>
          </div>
          <strong>{selectedCount} ausgewaehlt</strong>
        </div>
        <HistoryList
          entries={filtered}
          update={typeof safeUpdate === 'function' ? safeUpdate : () => Promise.resolve()}
          remove={typeof handleRemove === 'function' ? handleRemove : () => {}}
          userColors={colors}
          selectedIds={new Set(Object.keys(selectedMap || {}).map(k => Number(k)))}
          onToggleSelect={onToggleSelect}
          onRedeem={onRedeem}
        />
      </section>
    </main>
  );
}
